/**
 * The Reports tab of a job (fieldiq-api#1248, WT-3): the end-of-job report.
 *
 * LIFECYCLE (the API's): a DRAFT has no number and its content is built live
 * from the stored rows — that is the preview below each draft. ISSUING numbers
 * it WTR-NNNN, freezes the content and stores the PDF; it needs the job
 * COMPLETED, and the API's refusal (job_not_completed) is shown as a sentence
 * where the button was, never as a success. An issued report never changes; a
 * revision is a new draft.
 *
 * THE PDF opens as a blob through the client (claimBlobTab before the await,
 * so the popup is not blocked), the way the PF and NDT report links do. A
 * draft's PDF is stamped DRAFT by the API.
 *
 * Every write is WT_DISPATCH (the office); the buttons are hidden from the
 * other roles, who can still read the reports of a job they may see.
 */
import React, { useState } from 'react';
import { FileText, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../api/client';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import {
  useCreateWtReport,
  useDeleteWtReport,
  useIssueWtReport,
  useUpdateWtReport,
  useWtReadingFields,
  useWtReport,
  useWtReports,
} from '../../hooks/useWellTesting';
import { claimBlobTab, POPUP_BLOCKED_MESSAGE, showBlobInTab } from '../../lib/blob-tab';
import { formatInstant } from '../../lib/calendar-date';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import {
  blankToNull,
  fmtWtNumber,
  withUnit,
  wtQuantity,
  wtRefusal,
  wtReportStatusSkin,
  wtUnitLabel,
  WT_REPORT_REFUSALS,
} from '../../lib/well-testing';
import type { WtDisposition, WtJobDetail, WtMeta, WtReport, WtReportContent, WtReportValue } from '../../types/well-testing';
import { WT_INPUT, WT_PRIMARY, WT_SECONDARY, WtBadge, WtEmpty, WtField, WtFormError, WtLoading, WtPanel, WtSpec } from './WtBits';

/** Open a report's PDF in a new tab; a sentence on failure, null on success. */
export async function openWtReportPdf(reportId: string, win: Window | null): Promise<string | null> {
  if (!win) return POPUP_BLOCKED_MESSAGE;
  try {
    const res = await api.wellTesting.reports.pdf(reportId);
    showBlobInTab(win, res.data);
    return null;
  } catch (err) {
    win.close();
    return wtRefusal(err, 'The PDF could not be made.', WT_REPORT_REFUSALS);
  }
}

/** As typed, with its unit — the same rule as a reading row. */
const valueText = (v: WtReportValue | null | undefined) => (v ? wtQuantity({ entered: { value: v.value, unit: wtUnitLabel(v.unit) } }) : '—');
const PERIOD_LABEL: Record<string, string> = { cleanup: 'Cleanup', flow: 'Flow', shut_in: 'Shut-in', build_up: 'Build-up', drawdown: 'Drawdown' };

/** What the report holds (a draft's live preview or the issued content), as entered. */
function ReportContentSummary({ content }: { content: WtReportContent }) {
  const fields = useWtReadingFields().data?.data?.fields ?? [];
  const label = (key: string) => fields.find((f) => f.key === key)?.label ?? key;
  const tz = content.timeZone;
  const finals = content.final ? Object.entries(content.final.values) : [];
  const cums = Object.entries(content.cumulatives).filter(([, v]) => v);
  // The API converts the totals to the tenant's gas unit; older content has only the e3m3 copy.
  const totalText = (k: WtDisposition) => content.totalsDisplay
    ? withUnit(content.totalsDisplay[k], wtUnitLabel(content.totalsDisplay.unit))
    : withUnit(content.totals[k], wtUnitLabel('e3m3'));
  return (
    <div className="space-y-4 border-t border-fieldiq-border px-4 py-4" data-testid="wt-report-content">
      <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <WtSpec label="Readings" value={fmtWtNumber(content.readings.length)} mono />
        <WtSpec label="Flags" value={fmtWtNumber(content.flags.length)} mono />
        <WtSpec label="Load recovered" value={withUnit(content.load.recoveredPct, '%')} mono />
        <WtSpec label="Regulator" value={content.regulator ? `${content.regulator.regulator} (${content.regulator.jurisdiction})` : '—'} />
        <WtSpec label="Flared total" value={totalText('flared')} mono />
        <WtSpec label="Vented total" value={totalText('vented')} mono />
        <WtSpec label="Conserved total" value={totalText('conserved')} mono />
        <WtSpec label="Days" value={fmtWtNumber(content.dailyVolumes.length)} mono />
      </dl>
      {content.periods.length > 0 && (
        <div>
          <h3 className="mb-1 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Test periods</h3>
          <ul className="space-y-1 text-sm text-fieldiq-text">
            {content.periods.map((p, i) => (
              <li key={`${p.kind}-${i}`}>
                {PERIOD_LABEL[p.kind] ?? p.kind} · {formatInstant(p.startedAt, tz) ?? '—'} → {p.endedAt ? formatInstant(p.endedAt, tz) ?? '—' : 'open'} · {p.readings} readings
              </li>
            ))}
          </ul>
        </div>
      )}
      {content.final && (
        <div>
          <h3 className="mb-1 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
            Final rates · {formatInstant(content.final.readAt, tz) ?? '—'}
          </h3>
          <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {finals.map(([k, v]) => (
              <WtSpec key={k} label={label(k)} value={valueText(v)} mono />
            ))}
          </dl>
        </div>
      )}
      {cums.length > 0 && (
        <div>
          <h3 className="mb-1 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Cumulatives</h3>
          <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {cums.map(([k, v]) => (
              <WtSpec key={k} label={label(k)} value={valueText(v)} mono />
            ))}
          </dl>
        </div>
      )}
    </div>
  );
}

function ReportCard({ report, job, meta, canDispatch }: { report: WtReport; job: WtJobDetail; meta: WtMeta | null; canDispatch: boolean }) {
  const detail = useWtReport(report.id);
  const update = useUpdateWtReport();
  const issue = useIssueWtReport();
  const del = useDeleteWtReport();
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState('');
  const [comments, setComments] = useState('');
  const [summary, setSummary] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [confirming, setConfirming] = useState<'issue' | 'delete' | null>(null);
  const draft = report.status === 'draft';
  const tz = meta?.timeZone ?? null;
  const content = detail.data?.data?.content ?? detail.data?.data?.preview ?? null;
  const failure = queryLoadFailure(detail);

  const openPdf = async () => {
    setError(null);
    const win = claimBlobTab();
    setPdfBusy(true);
    const problem = await openWtReportPdf(report.id, win);
    setPdfBusy(false);
    if (problem) setError(problem);
  };

  return (
    <WtPanel
      eyebrow={draft ? 'Draft report' : 'Issued report'}
      title={report.reportNumber || report.title || 'End-of-job report'}
      testId="wt-report"
      actions={
        <div className="flex flex-wrap gap-2">
          <button type="button" className={WT_SECONDARY} onClick={openPdf} disabled={pdfBusy} data-testid="wt-report-pdf">
            <FileText className="size-4" aria-hidden="true" />
            {pdfBusy ? 'Opening…' : draft ? 'Preview PDF' : 'PDF'}
          </button>
          {draft && canDispatch && !editing && (
            <>
              <button
                type="button"
                className={WT_SECONDARY}
                onClick={() => {
                  setError(null);
                  setTitle(report.title ?? '');
                  setComments(report.comments ?? '');
                  setSummary(report.aiSummary ?? '');
                  setEditing(true);
                }}
              >
                Edit
              </button>
              <button type="button" className={WT_SECONDARY} onClick={() => { setError(null); setConfirming('delete'); }}>
                Delete draft
              </button>
              <button type="button" className={WT_PRIMARY} onClick={() => { setError(null); setConfirming('issue'); }} data-testid="wt-issue-report">
                Issue
              </button>
            </>
          )}
        </div>
      }
    >
      <div className="space-y-3 px-4 py-4" data-status={report.status}>
        <WtFormError message={error} />
        {draft && job.status !== 'completed' && (
          <p className="text-sm text-fieldiq-muted">The job is not completed yet. The report can be drafted and previewed now, and issued once the job is completed.</p>
        )}
        {confirming && (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-fi-control border border-fieldiq-border bg-fieldiq-dark/40 px-3 py-2" data-testid="wt-report-confirm">
            <p className="text-sm text-fieldiq-text">
              {confirming === 'issue'
                ? 'Issue this report? It is numbered, signed with your name and can no longer be changed.'
                : 'Delete this draft? Its content is rebuilt from the readings if you start a new one.'}
            </p>
            <div className="flex gap-2">
              <button type="button" className={WT_SECONDARY} onClick={() => setConfirming(null)} disabled={issue.isPending || del.isPending}>
                Not now
              </button>
              <button
                type="button"
                className={WT_PRIMARY}
                disabled={issue.isPending || del.isPending}
                data-testid={confirming === 'issue' ? 'wt-issue-confirm' : 'wt-delete-confirm'}
                onClick={() => {
                  setError(null);
                  if (confirming === 'issue') {
                    issue.mutate(report.id, {
                      onSuccess: (res) => {
                        toast.success(res.data?.reportNumber ? `Report ${res.data.reportNumber} issued` : 'Report issued');
                        setConfirming(null);
                      },
                      onError: (err) => {
                        setConfirming(null);
                        setError(wtRefusal(err, 'The report could not be issued.', WT_REPORT_REFUSALS));
                      },
                    });
                  } else {
                    del.mutate(report.id, {
                      onSuccess: () => toast.success('Draft deleted'),
                      onError: (err) => {
                        setConfirming(null);
                        setError(wtRefusal(err, 'The draft could not be deleted.', WT_REPORT_REFUSALS));
                      },
                    });
                  }
                }}
              >
                {confirming === 'issue' ? (issue.isPending ? 'Issuing…' : 'Issue report') : del.isPending ? 'Deleting…' : 'Delete draft'}
              </button>
            </div>
          </div>
        )}
        <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <WtSpec label="Report" value={report.reportNumber || 'Not numbered until issued'} mono />
          <WtSpec label="Status" value={<WtBadge skin={wtReportStatusSkin(report.status)} value={report.status} />} />
          <WtSpec label="Issued" value={formatInstant(report.issuedAt, tz) ?? '—'} />
          <WtSpec label="Signed by" value={report.issuedSignatoryName || '—'} />
          <WtSpec label="Title" value={report.title || '—'} />
          <WtSpec label="Created" value={formatInstant(report.createdAt, tz) ?? '—'} />
        </dl>
        {editing ? (
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              setError(null);
              update.mutate(
                { id: report.id, body: { title: blankToNull(title), comments: blankToNull(comments), ai_summary: blankToNull(summary) } },
                {
                  onSuccess: () => {
                    toast.success('Draft saved');
                    setEditing(false);
                  },
                  onError: (err) => setError(wtRefusal(err, 'The draft could not be saved.', WT_REPORT_REFUSALS)),
                },
              );
            }}
          >
            <WtField label="Title">
              <input className={WT_INPUT} aria-label="Report title" value={title} onChange={(e) => setTitle(e.target.value)} />
            </WtField>
            <WtField label="Summary">
              <textarea className={`${WT_INPUT} h-24 py-2`} aria-label="Report summary" value={summary} onChange={(e) => setSummary(e.target.value)} />
            </WtField>
            <WtField label="Comments">
              <textarea className={`${WT_INPUT} h-24 py-2`} aria-label="Report comments" value={comments} onChange={(e) => setComments(e.target.value)} />
            </WtField>
            <div className="flex justify-end gap-2">
              <button type="button" className={WT_SECONDARY} onClick={() => setEditing(false)} disabled={update.isPending}>
                Cancel
              </button>
              <button type="submit" className={WT_PRIMARY} disabled={update.isPending}>
                {update.isPending ? 'Saving…' : 'Save draft'}
              </button>
            </div>
          </form>
        ) : (
          <>
            {report.aiSummary && <p className="whitespace-pre-wrap text-sm text-fieldiq-text"><span className="font-bold">Summary: </span>{report.aiSummary}</p>}
            {report.comments && <p className="whitespace-pre-wrap text-sm text-fieldiq-text"><span className="font-bold">Comments: </span>{report.comments}</p>}
          </>
        )}
      </div>
      {detail.isLoading ? (
        <WtLoading label="Loading the report" />
      ) : failure ? (
        <LoadFailureNotice error={queryFailureError(detail)} kind={failure} subject="the report" />
      ) : content ? (
        <ReportContentSummary content={content} />
      ) : null}
    </WtPanel>
  );
}

export function ReportsSection({ job, meta, canDispatch }: { job: WtJobDetail; meta: WtMeta | null; canDispatch: boolean }) {
  const query = useWtReports(job.id);
  const create = useCreateWtReport();
  const [error, setError] = useState<string | null>(null);
  const reports = query.data?.data ?? [];
  const hasDraft = reports.some((r) => r.status === 'draft');
  const canCreate = canDispatch && !hasDraft && job.status !== 'draft' && job.status !== 'cancelled';
  const failure = queryLoadFailure(query);

  return (
    <>
      <WtPanel
        eyebrow="Reports"
        title="End-of-job report"
        testId="wt-reports"
        actions={
          canCreate ? (
            <button
              type="button"
              className={WT_PRIMARY}
              disabled={create.isPending}
              data-testid="wt-new-report"
              onClick={() => {
                setError(null);
                create.mutate(
                  { jobId: job.id, body: {} },
                  {
                    onSuccess: () => toast.success('Draft report started'),
                    onError: (err) => setError(wtRefusal(err, 'The draft could not be started.', WT_REPORT_REFUSALS)),
                  },
                );
              }}
            >
              <Plus className="size-4" aria-hidden="true" />
              {create.isPending ? 'Starting…' : 'New draft report'}
            </button>
          ) : undefined
        }
      >
        <div className="space-y-3 px-4 py-4">
          <WtFormError message={error} />
          {query.isLoading ? (
            <WtLoading label="Loading the reports" />
          ) : failure ? (
            <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="the reports" />
          ) : reports.length === 0 ? (
            <WtEmpty>
              {job.status === 'draft'
                ? 'This job is still a draft, so it has nothing to report yet.'
                : 'No report has been started for this job.'}
            </WtEmpty>
          ) : (
            <p className="text-sm text-fieldiq-muted">
              {reports.length} report{reports.length === 1 ? '' : 's'} · {reports.filter((r) => r.status === 'issued').length} issued
            </p>
          )}
        </div>
      </WtPanel>
      {reports.map((r) => (
        <ReportCard key={r.id} report={r} job={job} meta={meta} canDispatch={canDispatch} />
      ))}
    </>
  );
}
